// src/components/job-engine/WorkbenchPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Stack, Text, TextField, Dropdown, IDropdownOption, PrimaryButton, DefaultButton,
  MessageBar, MessageBarType, IconButton,
} from "@fluentui/react";
import { isAxiosError } from "axios";
import { publishWorkbench } from "@/api/jobEngine";
import type { JobState } from "@/api/jobEngine";
import { useJobTypesMeta } from "./useJobTypesMeta";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import PayloadEditor from "./PayloadEditor";
import StatePill from "./StatePill";
import useJobEngineEvent from "./useJobEngineEvent";

const isValidJson = (s: string): boolean => { try { JSON.parse(s); return true; } catch { return false; } };

const schema = Yup.object({
  jobType: Yup.string().required("Job type is required"),
  payload: Yup.string().required().test("valid-json", "Payload must be valid JSON", (v) => (v ? isValidJson(v) : false)),
  messageId: Yup.string().required(),
  correlationId: Yup.string().notRequired(),
  idempotencyKey: Yup.string().notRequired(),
});

interface FormValues {
  jobType: string;
  payload: string;
  messageId: string;
  correlationId: string;
  idempotencyKey: string;
}

interface ResultState {
  jobId: string;
  lastState: JobState | null;
}

const WorkbenchPage: React.FC = () => {
  const meta = useJobTypesMeta();
  const isConnected = useJobEngineConnectionState();
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);

  const newMessageId = useCallback(() => crypto.randomUUID(), []);

  const formik = useFormik<FormValues>({
    initialValues: { jobType: "", payload: "{}", messageId: newMessageId(), correlationId: "", idempotencyKey: "" },
    validationSchema: schema,
    onSubmit: async (values) => {
      setServerError(null);
      setResult(null);
      try {
        const resp = await publishWorkbench({
          jobType: values.jobType,
          payload: JSON.parse(values.payload),
          messageId: values.messageId,
          correlationId: values.correlationId || null,
          idempotencyKey: values.idempotencyKey || null,
        });
        setResult({ jobId: resp.jobId, lastState: null });
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 403) setServerError("You don't have permission to publish jobs.");
        else if (isAxiosError(err)) setServerError(err.response?.data?.title ?? "Publish failed");
        else setServerError("An unexpected error occurred.");
      }
    },
  });

  useJobEngineEvent<{ jobId: string }>("JobStarted",  (e) => { if (result && e.jobId === result.jobId) setResult({ ...result, lastState: "Started" }); });
  useJobEngineEvent<{ jobId: string; finalState: JobState }>("JobFinished", (e) => { if (result && e.jobId === result.jobId) setResult({ ...result, lastState: e.finalState }); });
  useJobEngineEvent<{ jobId: string }>("JobFailed",  (e) => { if (result && e.jobId === result.jobId) setResult({ ...result, lastState: "Failed" }); });
  useJobEngineEvent<{ jobId: string }>("JobDeadLettered", (e) => { if (result && e.jobId === result.jobId) setResult({ ...result, lastState: "DeadLettered" }); });

  const jobTypeOptions: IDropdownOption[] = useMemo(
    () => (meta.data?.jobTypes ?? []).map((t) => ({ key: t.jobType, text: t.jobType })),
    [meta.data],
  );

  useEffect(() => {
    if (result) formik.setFieldValue("messageId", newMessageId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.jobId]);

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: 820 } }}>
      <div className="page-header">
        <h2 className="page-title">Workbench</h2>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>
      <Text variant="small" styles={{ root: { color: "#605e5c" } }}>Publish a manual job for debugging or replay. Admin-only; unauthorized users see an access-denied message on submit.</Text>

      {serverError && <MessageBar messageBarType={MessageBarType.error}>{serverError}</MessageBar>}

      <form onSubmit={formik.handleSubmit}>
        <Stack tokens={{ childrenGap: 12 }}>
          <Dropdown
            label="Job type"
            required
            options={jobTypeOptions}
            selectedKey={formik.values.jobType}
            onChange={(_, o) => formik.setFieldValue("jobType", String(o?.key ?? ""))}
            errorMessage={formik.touched.jobType ? formik.errors.jobType : undefined}
          />
          <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>Payload (JSON)</Text>
            <PayloadEditor value={formik.values.payload} onChange={(v) => formik.setFieldValue("payload", v)} />
            {formik.touched.payload && formik.errors.payload && (
              <Text variant="small" styles={{ root: { color: "#d13438" } }}>{formik.errors.payload}</Text>
            )}
          </Stack>

          <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 8 }}>
            <TextField
              label="Message ID"
              value={formik.values.messageId}
              onChange={(_, v) => formik.setFieldValue("messageId", v ?? "")}
              styles={{ root: { flex: 1 } }}
            />
            <IconButton iconProps={{ iconName: "Refresh" }} title="Regenerate Message ID" onClick={() => formik.setFieldValue("messageId", newMessageId())} />
          </Stack>
          <TextField label="Correlation ID (optional)" value={formik.values.correlationId} onChange={(_, v) => formik.setFieldValue("correlationId", v ?? "")} />
          <TextField label="Idempotency key (optional)" value={formik.values.idempotencyKey} onChange={(_, v) => formik.setFieldValue("idempotencyKey", v ?? "")} />

          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton text="Publish" type="submit" disabled={!formik.isValid || formik.isSubmitting} />
            <DefaultButton text="Reset" onClick={() => formik.resetForm()} />
          </Stack>
        </Stack>
      </form>

      {result && (
        <Stack tokens={{ childrenGap: 6 }} styles={{ root: { padding: 12, border: "1px solid #edebe9", borderRadius: 4, background: "#faf9f8" } }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Published</Text>
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Text>Job ID:</Text>
            <Link to={`/job-engine/jobs/${encodeURIComponent(result.jobId)}`}>{result.jobId}</Link>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Text>Latest state:</Text>
            {result.lastState ? <StatePill state={result.lastState} /> : <Text variant="small">waiting…</Text>}
          </Stack>
        </Stack>
      )}
    </Stack>
    </div>
  );
};

export default WorkbenchPage;
